// Type definitions based on Talabat.WebApi Models

// Export auth types
export * from './auth';

// Export report types
export * from './reports';

export interface Branch {
  id: string;
  name: string;
  isActive: boolean;
  isSyncEnabled: boolean;
  isMenuSynced: boolean;
  
  // Talabat API fields
  cloudBranchId?: string;
  globalEntityId?: string;
  chainId?: string;
  chainName?: string;
  platformKey?: string;
  platformPhysicalVendorId?: string;
  secondsToAutoAccept?: number;
  transmissionFlow?: string;
  
  // Sync timestamps
  lastSyncedAt?: string;
  menuLastSyncedAt?: string;
  
  // Micros POS configuration fields
  localOrderType?: number;
  localRevenueCenterObjNum?: number;
  localTenderMediaObjNum?: number;
  localDiscountTenderMediaObjNum?: number;
  localCashTenderMediaObjNum?: number;
  localCreditCardTenderMediaObjNum?: number;
  autoCloseLocalOrder: boolean;
  localTableObjectNum?: number;
  localEmployeeObjectNum?: number;
  localMenuLevel?: number;
}

export interface MenuItemMapping {
  id: number;
  branchId: string;
  cloudMenuItemId: string;
  cloudMenuItemName: string;
  localMenuItemId: string;
  localMenuItemName?: string;
  localCategory?: string;
  localPrice?: number;
  isActive: boolean;
  lastUpdated: string;
  updatedBy?: string;
  createdAt: string;
  isModifier: boolean;
  isVerified: boolean;
  chainId?: string;
  cloudBranchId?: string;
  taxRate: number;
  branch?: Branch;
}

export interface ImportResult {
  success: boolean;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  errors: string[];
  message?: string;
}

export interface Order {
  id: string;
  cloudOrderId: string;
  cloudShortCode: string;
  branchId: string;
  cloudBranchId?: string;
  orderTime: string;
  customerName: string;
  status: OrderStatus; // Local POS sync status
  localOrderId?: string;
  localOrderNumber?: string;
  processedTime?: string;
  errorMessage?: string;
  total: number;
  totalItems: number;
  customerPhone?: string;
  isPaid: boolean;
  paymentMethod?: string;
  cloudOrderState?: string; // Cloud platform status (NEW, ACCEPTED, etc.)
  lastUpdated?: string;
  notes?: string;
  deliveryDetails?: string;
  pickupTime?: string;
  dispatchedAt?: string;
  deliveredAt?: string;
  branch?: Branch;
  items: OrderItem[];
  discounts: OrderDiscount[];
  taxes: OrderTax[];
}

export interface OrderItem {
  id: number;
  cloudMenuItemId: string;
  localMenuItemId?: string;
  name: string;
  quantity: number;
  price: number;
  priceWithoutTax: number;
  notes?: string;
  orderId?: string;
  modifiers: OrderItemModifier[];
}

export interface OrderItemModifier {
  id: number;
  cloudMenuItemId: string;
  localMenuItemId?: string;
  name: string;
  quantity: number;
  price: number;
  priceWithoutTax: number;
  orderItemId: number;
}

export interface OrderDiscount {
  id: number;
  name: string;
  value: number;
  orderId?: string;
}

export interface OrderTax {
  id: number;
  name: string;
  value: number;
  includedInPrice: boolean;
  orderId?: string;
}

export enum OrderStatus {
  New = 0,
  Pending = 1,
  SentToPOS = 2,
  Failed = 3,
  CancelledInPOS = 4,
  CancelFailed = 5,
  ClosedInPOS = 6,
  CloseFailed = 7
}

// Cloud platform order states from Talabat API
export enum CloudOrderState {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  CANCELLED_BY_TRANSPORT = 'CANCELLED_BY_TRANSPORT',
  CANCELLED_BY_PLATFORM = 'CANCELLED_BY_PLATFORM',
  CANCELLED_BY_VENDOR = 'CANCELLED_BY_VENDOR',
  
  
  // MODIFIED_BY_VENDOR = 'MODIFIED_BY_VENDOR',
  // RECONCILIATION_STARTED = 'RECONCILIATION_STARTED',
  // RECONCILED_WITH_PLATFORM = 'RECONCILED_WITH_PLATFORM',
  // RECONCILED_WITH_TRANSPORT = 'RECONCILED_WITH_TRANSPORT',
  // PREORDER_ACCEPTED = 'PREORDER_ACCEPTED',
  // WAITING_FOR_TRANSPORT = 'WAITING_FOR_TRANSPORT',

}

// Request/Response DTOs
export interface BranchSyncStatusUpdate {
  isSyncEnabled: boolean;
}

export interface BranchMenuSyncStatusUpdate {
  isMenuSynced: boolean;
}

export interface BranchPosConfig {
  localOrderType?: number;
  localRevenueCenterObjNum?: number;
  localTenderMediaObjNum?: number;
  localDiscountTenderMediaObjNum?: number;
  localCashTenderMediaObjNum?: number;
  localCreditCardTenderMediaObjNum?: number;
  autoCloseLocalOrder: boolean;
  localTableObjectNum?: number;
  localEmployeeObjectNum?: number;
  localMenuLevel?: number;
}

// API Response types
export interface ApiResponse<T> {
  data?: T;
  message?: string;
  success: boolean;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface MenuMappingsQuery {
  branchId?: string;
  searchTerm?: string;
  status?: string;
  pageNumber: number;
  pageSize: number;
}

// Form types for UI components
export interface BranchFormData extends Omit<Branch, 'id' | 'lastSyncedAt' | 'menuLastSyncedAt'> {}

export interface MenuItemMappingFormData extends Omit<MenuItemMapping, 'id' | 'lastUpdated' | 'createdAt' | 'branch'> {}

// Bulk operations types for menu item mappings
export interface CloudItemGrouping {
  cloudMenuItemName: string;
  totalCount: number;
  mappings: MenuItemMapping[];
  branchCounts: { [branchId: string]: number };
  branchNames: { [branchId: string]: string };
}

export interface BulkMappingSearchResult {
  groupings: CloudItemGrouping[];
  totalGroups: number;
  totalMappings: number;
}

export interface BulkMappingUpdateRequest {
  mappingIds: number[];
  updates: {
    localMenuItemId?: string;
    localMenuItemName?: string;
    taxRate?: number;
    isActive?: boolean;
  };
}

export interface BulkMappingUpdateResult {
  success: boolean;
  updatedCount: number;
  errors: string[];
  message?: string;
}

// Filter types
export interface BranchFilter {
  isActive?: boolean;
  isSyncEnabled?: boolean;
  isMenuSynced?: boolean;
  chainId?: string;
}

export interface OrderFilter {
  branchId?: string;
  status?: OrderStatus[];
  dateFrom?: string;
  dateTo?: string;
  customerName?: string;
}

export interface MappingFilter {
  branchId?: string;
  isActive?: boolean;
  isModifier?: boolean;
  isVerified?: boolean;
}

export interface SyncDetails {
  syncedCount: number;
  regularItemsCount: number;
  modifiersCount: number;
}

export interface SyncAllResult {
  message: string;
  branchId: string;
  cloudBranchId: string;
  menuItems: SyncDetails;
  toppings: SyncDetails;
  totalSyncedCount: number;
}

export interface SyncAllBranchesResult {
  message: string;
  syncedCount: number;
  activeBranchesCount: number;
  branches: Branch[];
}

export interface OrderCountersOnly {
  new: number;
  sentToPOS: number;
  failed: number;
  cancelled: number;
  closed: number;
}

export interface OrderCounters {
  counters: {
    new: number;
    sentToPOS: number;
    failed: number;
    cancelled: number;
    closed: number;
  };
  sales: {
    new: number;
    sentToPOS: number;
    failed: number;
    cancelled: number;
    closed: number;
  };
  talabatSales: {
    new: number;
    sentToPOS: number;
    failed: number;
    cancelled: number;
    closed: number;
  };
  discounts: {
    new: number;
    sentToPOS: number;
    failed: number;
    cancelled: number;
    closed: number;
  };
  paymentSummary: {
    prepaid: {
      count: number;
      sales: number;
      talabatSales: number;
      discounts: number;
    };
    cash: {
      count: number;
      sales: number;
      talabatSales: number;
      discounts: number;
    };
  };
}

export interface DashboardSettings {
  intervalDays: number;
  autoRefresh: boolean;
  refreshInterval: number; // in seconds
}

export interface BranchSummary {
  total: number;
  active: number;
  syncEnabled: number;
  menuSynced: number;
}

export interface OrderSummary {
  id: string;
  orderTime: string;
  totalAmount: number;
  status: OrderStatus;
  branchId: string;
  branchName?: string;
  cloudOrderId: string;
  cloudShortCode: string;
  customerName?: string;
  cloudOrderState?: string;
}